import { useEffect, useMemo, useState } from 'react';
import { useRepoContext } from '../contexts/RepoContext';
import { useAuth } from '../contexts/AuthContext';
import { apiCall, ApiError } from '../utils/apiHelpers';

type NavContentType = 'md' | 'code' | 'gsheet' | 'gslide' | 'pdf' | 'text';

interface TreeFile {
    path: string;
    contentType: NavContentType;
    title?: string;
    sizeBytes: number;
}

const CONTENT_ICONS: Record<NavContentType, string> = {
    md: '📄',
    code: '<>',
    gsheet: '📊',
    gslide: '🖼',
    pdf: '📕',
    text: '📃'
};

interface TreeResponse {
    commitSha: string | null;
    lastIndexedAt: string | null;
    files: TreeFile[];
    truncated?: boolean;
    cap?: number;
}

type FileNode = { type: 'file'; path: string; name: string; file: TreeFile };
type DirNode = { type: 'dir'; name: string; path: string; children: TreeNode[] };
type TreeNode = FileNode | DirNode;

interface Props {
    selectedPath: string | null;
    onSelectPath: (path: string) => void;
}

/**
 * Pick the file to auto-open when a repo is first selected. Mirrors GitHub's
 * README discovery: case-insensitive `readme.md` (or `.markdown` / `.mdx`),
 * preferring the shortest path (root-most one in the indexed scope).
 */
function findReadme(files: TreeFile[]): TreeFile | null {
    let best: TreeFile | null = null;
    for (const f of files) {
        const lower = f.path.toLowerCase();
        const segs = f.path.split('/');
        const name = (segs[segs.length - 1] ?? '').toLowerCase();
        if (
            name === 'readme.md' ||
            name === 'readme.markdown' ||
            name === 'readme.mdx' ||
            name === 'readme'
        ) {
            if (!best || segs.length < best.path.split('/').length) best = f;
        } else if (!best && (lower === 'index.md' || lower === 'index.mdx')) {
            best = f;
        }
    }
    return best;
}

/**
 * The file the viewer should auto-open when a repo loads. Prefer a README
 * (see `findReadme`); if there isn't one, fall back to the shallowest-then-
 * alphabetical markdown file in the indexed scope so the reviewer lands on
 * prose rather than an empty viewer. If the indexed set has no markdown at
 * all (e.g. pdf-only repo, attachments-only Drive folder), this returns
 * null and the viewer is left empty — the file tree on the left still
 * lets the reviewer pick something manually.
 *
 * We only fall back to `contentType === 'md'` — not pdf/gsheet/gslide/text
 * — because those other types are usually attachments or data files; a
 * reviewer expects to land on prose. (Exported Google Docs come through
 * as `contentType: 'md'`, so Drive sources are covered too.)
 *
 * The tie-break uses `localeCompare` so default-file selection matches the
 * tree's `sortTree()` ordering on the left rail — the auto-opened file is
 * the one a reviewer would visually identify as "first" in the tree.
 *
 * `res.files` is already role-ACL-filtered server-side (`repo-tree.ts`
 * shares predicate semantics with `roles.ts:matchesGrantScope`), so this
 * fallback cannot pick a path the reviewer can't open.
 */
function findDefaultFile(files: TreeFile[]): TreeFile | null {
    const readme = findReadme(files);
    if (readme) return readme;

    let best: TreeFile | null = null;
    let bestDepth = Number.POSITIVE_INFINITY;
    for (const f of files) {
        if (f.contentType !== 'md') continue;
        const depth = f.path.split('/').length;
        if (!best || depth < bestDepth || (depth === bestDepth && f.path.localeCompare(best.path) < 0)) {
            best = f;
            bestDepth = depth;
        }
    }
    return best;
}

function buildTree(files: TreeFile[]): DirNode {
    const root: DirNode = { type: 'dir', name: '', path: '', children: [] };
    // Use a map at each dir to dedupe by name during construction.
    const dirIndex: Record<string, DirNode> = { '': root };
    for (const f of files) {
        const segments = f.path.split('/');
        let parentPath = '';
        let parent = root;
        for (let i = 0; i < segments.length - 1; i++) {
            const seg = segments[i];
            const childPath = parentPath ? parentPath + '/' + seg : seg;
            let dir = dirIndex[childPath];
            if (!dir) {
                dir = { type: 'dir', name: seg, path: childPath, children: [] };
                dirIndex[childPath] = dir;
                parent.children.push(dir);
            }
            parent = dir;
            parentPath = childPath;
        }
        const fileName = segments[segments.length - 1];
        parent.children.push({ type: 'file', path: f.path, name: fileName, file: f });
    }
    sortTree(root);
    return root;
}

function sortTree(dir: DirNode) {
    dir.children.sort((a, b) => {
        // dirs before files, then alpha
        if (a.type !== b.type) return a.type === 'dir' ? -1 : 1;
        return a.name.localeCompare(b.name);
    });
    for (const c of dir.children) if (c.type === 'dir') sortTree(c);
}

function filterTree(node: DirNode, q: string): DirNode | null {
    if (!q) return node;
    const lower = q.toLowerCase();
    function matches(file: TreeFile): boolean {
        return (
            file.path.toLowerCase().includes(lower) ||
            (file.title?.toLowerCase().includes(lower) ?? false)
        );
    }
    function rec(n: TreeNode): TreeNode | null {
        if (n.type === 'file') return matches(n.file) ? n : null;
        const kept: TreeNode[] = [];
        for (const c of n.children) {
            const r = rec(c);
            if (r) kept.push(r);
        }
        if (kept.length === 0) return null;
        return { type: 'dir', name: n.name, path: n.path, children: kept };
    }
    return rec(node) as DirNode | null;
}

function collectDirs(node: DirNode, out: Set<string>) {
    out.add(node.path);
    for (const c of node.children) if (c.type === 'dir') collectDirs(c, out);
}

function TreeNodeView({
    node,
    selectedPath,
    onSelectPath,
    expanded,
    onToggle,
    depth
}: {
    node: TreeNode;
    selectedPath: string | null;
    onSelectPath: (p: string) => void;
    expanded: Set<string>;
    onToggle: (path: string) => void;
    depth: number;
}) {
    if (node.type === 'file') {
        const active = selectedPath === node.path;
        return (
            <li className={'tree-file' + (active ? ' tree-file--active' : '')}>
                <button
                    type="button"
                    style={{ paddingLeft: `${depth * 14 + 8}px` }}
                    onClick={() => onSelectPath(node.path)}
                    title={node.path}
                >
                    <span className={'tree-icon tree-icon--' + node.file.contentType}>
                        {CONTENT_ICONS[node.file.contentType] ?? '📄'}
                    </span>
                    <span className="tree-file__name">{node.file.title ?? node.name}</span>
                </button>
            </li>
        );
    }
    const isOpen = expanded.has(node.path);
    return (
        <li className="tree-dir">
            <button
                type="button"
                className="tree-dir__toggle"
                style={{ paddingLeft: `${depth * 14 + 4}px` }}
                onClick={() => onToggle(node.path)}
                aria-expanded={isOpen}
            >
                <span className="tree-caret">{isOpen ? '▾' : '▸'}</span>
                <span className="tree-dir__name">{node.name || '/'}</span>
            </button>
            {isOpen && (
                <ul className="tree-list">
                    {node.children.map((c) => (
                        <TreeNodeView
                            key={(c.type === 'dir' ? 'd:' : 'f:') + c.path}
                            node={c}
                            selectedPath={selectedPath}
                            onSelectPath={onSelectPath}
                            expanded={expanded}
                            onToggle={onToggle}
                            depth={depth + 1}
                        />
                    ))}
                </ul>
            )}
        </li>
    );
}

export function RepoNav({ selectedPath, onSelectPath }: Props) {
    const { allowedRepos, effectiveRepoId, selectRepo } = useRepoContext();
    const { tokenReady } = useAuth();
    const [tree, setTree] = useState<TreeResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState('');
    const [debouncedFilter, setDebouncedFilter] = useState('');
    const [searching, setSearching] = useState(false);
    const [expanded, setExpanded] = useState<Set<string>>(new Set(['']));

    // Debounce the filter so we don't fire a keyword search on every keystroke.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedFilter(filter.trim()), 300);
        return () => clearTimeout(t);
    }, [filter]);

    useEffect(() => {
        if (!tokenReady || allowedRepos === undefined || !effectiveRepoId) {
            setTree(null);
            return;
        }
        let cancelled = false;
        const isSearch = debouncedFilter.length >= 2;
        if (isSearch) setSearching(true);
        (async () => {
            try {
                const [owner, name] = effectiveRepoId.split('/');
                const qs = isSearch ? `?q=${encodeURIComponent(debouncedFilter)}` : '';
                const res = await apiCall<TreeResponse>(
                    `/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/tree${qs}`
                );
                if (!cancelled) {
                    setTree(res);
                    setError(null);
                    if (isSearch) {
                        // Expand every dir so search hits are visible.
                        const dirs = new Set<string>(['']);
                        for (const f of res.files) {
                            const parts = f.path.split('/');
                            for (let i = 1; i < parts.length; i++) {
                                dirs.add(parts.slice(0, i).join('/'));
                            }
                        }
                        setExpanded(dirs);
                    } else {
                        // Expand top-level dirs by default for instant visibility.
                        const rootDirs = new Set<string>(['']);
                        for (const f of res.files) {
                            const first = f.path.split('/')[0];
                            if (f.path.includes('/')) rootDirs.add(first);
                        }
                        setExpanded(rootDirs);
                        // Auto-open the README on first load, or the
                        // shallowest .md if no README is present.
                        if (!selectedPath) {
                            const defaultFile = findDefaultFile(res.files);
                            if (defaultFile) onSelectPath(defaultFile.path);
                        }
                    }
                }
            } catch (err) {
                if (cancelled) return;
                setTree(null);
                setError(err instanceof ApiError ? err.message : (err as Error).message);
            } finally {
                if (!cancelled) setSearching(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokenReady, allowedRepos, effectiveRepoId, debouncedFilter]);

    const fullTree = useMemo(() => (tree ? buildTree(tree.files) : null), [tree]);
    // When we're in server-search mode, the tree we got back IS already the
    // filtered set (body matches included). Running the client-side path/title
    // filter on top would drop body-only hits — show the server result as-is.
    const isServerSearchMode = debouncedFilter.length >= 2;
    const visibleTree = useMemo(
        () => (fullTree ? (isServerSearchMode ? fullTree : filterTree(fullTree, filter)) : null),
        [fullTree, filter, isServerSearchMode]
    );

    function toggle(path: string) {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(path)) next.delete(path);
            else next.add(path);
            return next;
        });
    }

    function expandAll() {
        if (!visibleTree) return;
        const all = new Set<string>();
        collectDirs(visibleTree, all);
        setExpanded(all);
    }
    function collapseAll() {
        setExpanded(new Set(['']));
    }

    if (allowedRepos === undefined) {
        return <div className="repo-nav__placeholder">Loading repos…</div>;
    }
    if (allowedRepos.length === 0) {
        return (
            <div className="repo-nav__placeholder">
                No repos available.<br />
                Switch to <strong>Admin → Repos</strong> if you're an admin, or contact your administrator.
            </div>
        );
    }

    return (
        <div className="repo-nav">
            <label className="repo-nav__selector-label" htmlFor="repo-select">Repository</label>
            <select
                id="repo-select"
                className="repo-nav__selector"
                value={effectiveRepoId ?? ''}
                onChange={(e) => selectRepo(e.target.value || null)}
                title={effectiveRepoId ?? ''}
            >
                {allowedRepos.map((r) => {
                    const prefix = r.sourceKind === 'gdrive' ? '🗂  ' : '🐙  ';
                    return (
                        <option key={r.id} value={r.id} title={r.id}>
                            {prefix}{r.name}
                        </option>
                    );
                })}
            </select>

            <input
                type="search"
                className="repo-nav__filter"
                placeholder={searching ? 'Searching…' : 'Search files & content…'}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                aria-label="Search files and contents"
            />

            <div className="tree-actions">
                <button type="button" className="btn btn--ghost btn--small" onClick={expandAll}>Expand all</button>
                <button type="button" className="btn btn--ghost btn--small" onClick={collapseAll}>Collapse all</button>
            </div>

            {error && <div className="repo-nav__error">{error}</div>}
            {!tree && !error && effectiveRepoId && <div className="repo-nav__placeholder">Loading files…</div>}
            {tree && tree.files.length === 0 && (
                <div className="repo-nav__placeholder">No files indexed yet.</div>
            )}

            {visibleTree && (
                <ul className="tree-list tree-list--root">
                    {visibleTree.children.map((c) => (
                        <TreeNodeView
                            key={(c.type === 'dir' ? 'd:' : 'f:') + c.path}
                            node={c}
                            selectedPath={selectedPath}
                            onSelectPath={onSelectPath}
                            expanded={expanded}
                            onToggle={toggle}
                            depth={0}
                        />
                    ))}
                </ul>
            )}
        </div>
    );
}
